import type { KemMessageWithRelations } from "@ch/generated";
import { KemMessageControllerService } from "@ch/generated";
import type { TPagedData } from "@ch/pages/MindfulnessPage/hooks/types";
import { useCallback, useRef } from "react";
import {
  parseKemCitationResources,
  pickKemCitationSource,
} from "../utils/kemCitations";
import type { TKemMessagePayload } from "../utils/types";

const extractKemMessageList = (raw: unknown): KemMessageWithRelations[] => {
  if (Array.isArray(raw)) {
    return raw as KemMessageWithRelations[];
  }
  if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    if (Array.isArray(o.data)) {
      return o.data as KemMessageWithRelations[];
    }
    const nested = o.data;
    if (nested && typeof nested === "object") {
      const inner = nested as Record<string, unknown>;
      if (Array.isArray(inner.data)) {
        return inner.data as KemMessageWithRelations[];
      }
    }
  }
  return [];
};

const mapHistoryMessage = (
  message: KemMessageWithRelations,
): TKemMessagePayload => ({
  ...message,
  resources:
    message.sender_enum === "kem"
      ? parseKemCitationResources(pickKemCitationSource(message))
      : undefined,
});

const mapHistoryMessages = (
  list: KemMessageWithRelations[],
): TKemMessagePayload[] => list.map(mapHistoryMessage);

export interface UseGetHistoryProps<T = unknown> {
  onSuccessCallback?: (response: T) => void;
  pageNumber?: number;
  pageLimit?: number;
  userId?: string;
  sessionKey?: string;
  enabled?: boolean;
  findBySessionKey?: boolean;
}

const useGetHistory = ({
  onSuccessCallback,
  pageNumber = 1,
  pageLimit = 10,
  sessionKey,
  enabled = true,
  findBySessionKey = false,
}: UseGetHistoryProps<TPagedData<TKemMessagePayload>> = {}) => {
  const safePage = Math.max(1, Math.floor(pageNumber || 1));
  const safeLimit = Math.max(1, Math.floor(pageLimit || 10));

  const currentPage = useRef(safePage);
  const totalPages = useRef<number | undefined>(undefined);
  const lastBatchCount = useRef<number>(0);

  const hasFetchedInitialData = useRef(false);

  const sessionFetchIdentity = `${sessionKey ?? ""}:${findBySessionKey ? "bySession" : "filter"}`;
  const paginationEpochKey = `${sessionFetchIdentity}:${safePage}`;
  const lastPaginationEpochKeyRef = useRef<string | null>(null);

  const getIsLastPage = useCallback(() => {
    if (!enabled) return true;

    if (totalPages.current !== undefined) {
      if (totalPages.current === 0) return true;
      return currentPage.current >= totalPages.current;
    }
    return lastBatchCount.current > 0 && lastBatchCount.current < safeLimit;
  }, [safeLimit, enabled]);

  const getChatHistory = useCallback(
    async (limitCount = safeLimit) => {
      if (!enabled) {
        totalPages.current = 0;
        return [];
      }

      if (lastPaginationEpochKeyRef.current !== paginationEpochKey) {
        lastPaginationEpochKeyRef.current = paginationEpochKey;
        currentPage.current = safePage;
        totalPages.current = undefined;
        lastBatchCount.current = 0;
        hasFetchedInitialData.current = false;
      }

      const validLimit = Math.max(1, Math.floor(limitCount || safeLimit || 10));

      const validPage = Math.max(1, Math.floor(currentPage.current || 1));

      const useSessionKeyPath = findBySessionKey && sessionKey;
      const resp = useSessionKeyPath
        ? await KemMessageControllerService.kemMessageControllerFindBySessionKey(
            {
              sessionKey,
              filter: { limit: validLimit },
              page: validPage,
            },
          )
        : await KemMessageControllerService.kemMessageControllerFindAll({
            filter: {
              limit: validLimit,
              ...(sessionKey && !findBySessionKey
                ? { where: { session_key: sessionKey } }
                : {}),
            },
            page: validPage,
          });

      const raw: unknown = resp as unknown;
      const maybePaged = raw as {
        data?: unknown;
        totalPages?: number;
        currentPage?: number;
        count?: number;
        status?: string;
      };

      const list = mapHistoryMessages(extractKemMessageList(raw));

      const hasPagedMeta =
        typeof maybePaged === "object" &&
        maybePaged !== null &&
        (typeof maybePaged.totalPages === "number" ||
          typeof maybePaged.currentPage === "number");

      const hasDataArray =
        typeof maybePaged === "object" &&
        maybePaged !== null &&
        Array.isArray(maybePaged.data);

      if (hasDataArray || hasPagedMeta) {
        if (typeof maybePaged.totalPages === "number") {
          totalPages.current = maybePaged.totalPages;
        }
        if (typeof maybePaged.currentPage === "number") {
          currentPage.current = maybePaged.currentPage;
        }
      }

      lastBatchCount.current = list.length;

      const body: TPagedData<TKemMessagePayload> = {
        data: list,
        totalPages:
          typeof maybePaged.totalPages === "number"
            ? maybePaged.totalPages
            : (totalPages.current ?? 0),
        currentPage:
          typeof maybePaged.currentPage === "number"
            ? maybePaged.currentPage
            : currentPage.current,
        count:
          typeof maybePaged.count === "number" ? maybePaged.count : list.length,
        status: (typeof maybePaged.status === "string"
          ? maybePaged.status
          : "success") as TPagedData<TKemMessagePayload>["status"],
      };

      onSuccessCallback?.(body);

      if (!hasFetchedInitialData.current) {
        hasFetchedInitialData.current = true;
      }

      return list;
    },
    [
      enabled,
      findBySessionKey,
      onSuccessCallback,
      paginationEpochKey,
      safeLimit,
      safePage,
      sessionKey,
    ],
  );

  const getNextChatHistory = useCallback(async () => {
    if (getIsLastPage()) {
      return [] as TKemMessagePayload[];
    }

    currentPage.current = Math.max(1, currentPage.current + 1);

    return getChatHistory();
  }, [getChatHistory, getIsLastPage]);

  return {
    getIsLastPage,
    getChatHistory,
    getNextChatHistory,
    currentPage,
    totalPages,
    hasFetchedInitialData,
  };
};

export default useGetHistory;
